import { useEffect, useState } from 'react';
import { getHomeRates } from '../../api/sparksApi';

/**
 * Home Learning Sparks rates as the server decides them (GET /sparks/:childId/home-rates, docs/sparks-home-learning-rates.md):
 * per lesson, the child's colour (`band`) and the score behind it (`bandPercent`), how many years the lesson sits
 * below (+) or above (−) them (`yearOffset`), and the `multiplier` every Sparks award there gets today.
 */
export const RATE_COPY = {
  grey: 'New adventure! Full Sparks ✨',
  red: 'Tricky one! Bonus Sparks ⚡',
  amber: 'Nearly there! Keep going 💪',
  green: "You've mastered this! Fewer Sparks here. Try a trickier one",
  above: 'Super challenge! Extra Sparks 🚀',
  warmUp: 'Warm-up lesson: fewer Sparks',
  tipTitle: "Addy's Sparks tip!",
  tip: "Tricky topics give you BONUS Sparks ⚡ Turn a red topic green to win a Level-Up bonus! Topics you've mastered give fewer Sparks, so go find your next challenge!",
};

/** Matches the progress dashboard's RAGCircle colours. */
export const BAND_BAR = { red: 'bg-red-500', amber: 'bg-amber-400', green: 'bg-green-500', grey: 'bg-gray-300' };

/**
 * The one tag a lesson card shows. Red and green always speak for themselves; for a new or amber lesson the year
 * group is the bigger story, so an above-level or 2+ years below lesson says that instead.
 */
export function rateTag(rate) {
  if (!rate) return null;
  if (rate.band === 'red' || rate.band === 'green') return { key: rate.band, text: RATE_COPY[rate.band] };
  if (rate.yearOffset < 0) return { key: 'above', text: RATE_COPY.above };
  if (rate.yearOffset >= 2) return { key: 'warmUp', text: RATE_COPY.warmUp };
  return { key: rate.band, text: RATE_COPY[rate.band] };
}

/** "×1.9", or '' for a normal rate. One decimal place, so a tiny rate still reads as something (×0.1). */
export function formatMultiplier(multiplier) {
  const m = Number(multiplier);
  if (!Number.isFinite(m) || m === 1) return '';
  return `×${Math.max(0.1, Math.round(m * 10) / 10)}`;
}

const TIP_SHOW_AGAIN_MS = 7 * 24 * 3_600_000;
const tipKey = (childId) => `sparksRatesTip:${childId}`;

/** The tip banner shows until the child closes it, then again a week after. Storage failing just means it shows. */
export function tipDue(childId, now = Date.now()) {
  try {
    const closedAt = Number(window.localStorage.getItem(tipKey(childId)));
    return !closedAt || now - closedAt >= TIP_SHOW_AGAIN_MS;
  } catch {
    return true;
  }
}

export function rememberTipClosed(childId) {
  try {
    window.localStorage.setItem(tipKey(childId), String(Date.now()));
  } catch {
    // Not stored: the tip simply comes back next visit.
  }
}

/** The child's Home Learning rates, or null while loading and on any failure (cards then show nothing new). */
export default function useHomeRates(childId) {
  const [rates, setRates] = useState(null);
  useEffect(() => {
    setRates(null);
    if (!childId) return undefined;
    let cancelled = false;
    getHomeRates(childId)
      .then((r) => { if (!cancelled) setRates(r); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [childId]);
  return rates;
}
